import { useId, useState } from 'react'
import { useIntl } from 'react-intl'

import { SUPPORT_TEXT_MAX_LENGTH, type SupportLine, type SupportStatus, valianAfterStatusesOf } from '@onr/schema'
import { handleNotAwaitedPromise } from '@valian/react-kitchen-sink'
import { LockIcon } from 'lucide-react'

import { Button } from '@/components/atoms/Button/Button.tsx'
import { CharacterCounter } from '@/components/atoms/CharacterCounter'
import { InlineError } from '@/components/atoms/InlineError'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/atoms/Select/Select.tsx'
import { TextArea } from '@/components/atoms/TextArea/TextArea.tsx'
import { SUPPORT_COUNTER_THRESHOLD } from '@/components/organisms/SupportRequestForm'
import { cn } from '@/lib/utils.ts'

export type SupportAgentSendMode = 'reply' | 'note'

const STATUS_ORDER = [
  'toHandle',
  'waitingValian',
  'waitingRequester',
  'settled',
] as const satisfies ReadonlyArray<SupportStatus>

const defaultAfterStatus = (mode: SupportAgentSendMode, status: SupportStatus): SupportStatus =>
  mode === 'reply' ? 'waitingRequester' : status

const afterStatusOptions = (line: SupportLine, mode: SupportAgentSendMode, status: SupportStatus): SupportStatus[] => {
  if (line === 'l2') {
    return valianAfterStatusesOf(status)
  }
  const first = defaultAfterStatus(mode, status)
  return [first, ...STATUS_ORDER.filter((option) => option !== first)]
}

export interface SupportAgentComposerProps {
  readonly line: SupportLine
  readonly status: SupportStatus
  readonly signature: string
  readonly sending: boolean
  readonly error: boolean
  readonly onSend: (send: { mode: SupportAgentSendMode; text: string; afterStatus: SupportStatus }) => Promise<boolean>
}

export const SupportAgentComposer = ({
  line,
  status,
  signature,
  sending,
  error,
  onSend,
}: SupportAgentComposerProps) => {
  const intl = useIntl()
  const textId = useId()
  const afterSendId = useId()
  const [mode, setMode] = useState<SupportAgentSendMode>(line === 'l2' ? 'note' : 'reply')
  const [text, setText] = useState('')
  const [chosenAfterStatus, setChosenAfterStatus] = useState<SupportStatus | null>(null)
  const valian = line === 'l2'
  const note = mode === 'note'
  const afterStatus = chosenAfterStatus ?? defaultAfterStatus(mode, status)

  const switchMode = (next: SupportAgentSendMode) => {
    setMode(next)
    setChosenAfterStatus(null)
  }

  const statusLabel = (option: SupportStatus) => {
    if (valian && status === 'waitingValian' && (option === 'toHandle' || option === 'waitingValian')) {
      return intl.formatMessage({ id: `soutien.composer.valian.${option}` })
    }
    const label = intl.formatMessage({ id: `soutien.status.${option}` })
    return note && option === status
      ? intl.formatMessage({ id: 'soutien.composer.unchanged' }, { status: label })
      : label
  }

  const submit = async () => {
    if (await onSend({ mode, text: text.trim(), afterStatus })) {
      setText('')
      if (valian) {
        setChosenAfterStatus(null)
      } else {
        switchMode('reply')
      }
    }
  }

  const tabClassName = (selected: boolean) =>
    cn(
      'inline-flex h-[30px] cursor-pointer items-center gap-1.5 rounded-[6px] px-3.5 text-base',
      selected ? 'bg-white font-bold text-gray-900 shadow-sm' : 'text-gray-600'
    )

  return (
    <form
      className={cn('flex flex-col gap-2.5 border-t border-gray-200 px-6 pt-8 pb-4', note ? 'bg-gray-100' : 'bg-white')}
      onSubmit={(event) => {
        event.preventDefault()
        if (text.trim() === '' || sending) {
          return
        }
        handleNotAwaitedPromise(submit())
      }}>
      {valian ? (
        <span className="inline-flex items-center gap-1.5 font-bold text-gray-800">
          <LockIcon aria-hidden className="size-[13px]" />
          {intl.formatMessage({ id: 'soutien.composer.note' })}
        </span>
      ) : (
        <div
          role="tablist"
          aria-label={intl.formatMessage({ id: 'soutien.composer.mode' })}
          className="inline-flex gap-0.5 self-start rounded-[8px] bg-gray-200 p-[3px]">
          <button
            type="button"
            role="tab"
            aria-selected={!note}
            className={tabClassName(!note)}
            onClick={() => switchMode('reply')}>
            {intl.formatMessage({ id: 'soutien.composer.reply' })}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={note}
            className={tabClassName(note)}
            onClick={() => switchMode('note')}>
            <LockIcon aria-hidden className="size-[13px]" />
            {intl.formatMessage({ id: 'soutien.composer.note' })}
          </button>
        </div>
      )}
      <TextArea
        id={textId}
        rows={3}
        value={text}
        maxLength={SUPPORT_TEXT_MAX_LENGTH}
        placeholder={intl.formatMessage({
          id: valian
            ? 'soutien.composer.valian.placeholder'
            : note
              ? 'soutien.composer.note.placeholder'
              : 'soutien.composer.reply.placeholder',
        })}
        className={cn(
          'resize-y rounded-[4px] bg-white py-2 text-base leading-[1.45] text-gray-900 placeholder:text-gray-400',
          note ? 'border-dashed border-gray-600' : 'border-gray-300'
        )}
        onChange={(event) => setText(event.target.value)}
      />
      <label htmlFor={textId} className="text-sm text-gray-700">
        {valian
          ? intl.formatMessage({ id: 'soutien.composer.valian.help' })
          : note
            ? intl.formatMessage({ id: 'soutien.composer.note.help' })
            : intl.formatMessage({ id: 'soutien.composer.reply.help' }, { signature })}
      </label>
      <CharacterCounter length={text.length} max={SUPPORT_TEXT_MAX_LENGTH} showFrom={SUPPORT_COUNTER_THRESHOLD} />
      {error && <InlineError message={intl.formatMessage({ id: 'soutien.composer.error' })} />}
      <div className="flex flex-wrap items-center justify-end gap-3">
        <div className="inline-flex items-center gap-2">
          <label id={afterSendId} className="text-sm text-gray-700">
            {intl.formatMessage({ id: 'soutien.composer.afterSend' })}
          </label>
          <Select value={afterStatus} onValueChange={(value) => setChosenAfterStatus(value as SupportStatus)}>
            <SelectTrigger
              variant="field"
              aria-labelledby={afterSendId}
              className="h-9 w-auto min-w-[220px] text-gray-900">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {afterStatusOptions(line, mode, status).map((option) => (
                <SelectItem key={option} value={option}>
                  {statusLabel(option)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          type="submit"
          variant="primary"
          className={cn('h-10 px-6', note && 'bg-gray-700 hover:bg-gray-600')}
          loading={sending}
          disabled={text.trim() === ''}>
          {intl.formatMessage({ id: note ? 'soutien.composer.note.submit' : 'soutien.composer.reply.submit' })}
        </Button>
      </div>
    </form>
  )
}
